import { createBus } from 'suber'
import _ from 'common-tools/LodashHelper'
import React, { useEffect, useRef } from 'react'
import { log } from 'common-tools/LogHelper'

const uuidv4 = require('uuid/v4')
const appBus = createBus()

export const MessageBusTypes = {
  DrawEvents: 'DrawerEvents'
}

export const MessageBus = {
  subscribe: (type, callback) => {
    return appBus.take(type, callback)
  },
  send: (type, message, key) => {
    if (message) {
      appBus.send(type, message)
    }
  }
}

// Hooks API
export function useMessageBusListen ({ onNewMessage, messageBusKey, listenForAllMessages = false, busType = MessageBusTypes.DrawEvents }) {
  var busCancel

  const listenKey = useRef(messageBusKey || uuidv4())

  function onEvents (events) {
    console.log('Message Fired')
    if (_.isFunction(onNewMessage)) {
      for (var event of events.actions) {
        if (listenForAllMessages === true) {
          onNewMessage(event)
        } else if (events.messageBusKey === listenKey.current) {
          onNewMessage(event)
        }
      }
    }
  }

  useEffect(() => {
    busCancel = MessageBus.subscribe(busType, onEvents)
    return () => {
      busCancel()
    }
  }, [])

  return [listenKey.current]
}

export function useMessageBusPublish ({ messageBusKey, busType = MessageBusTypes.DrawEvents }) {
  var listenKey = messageBusKey

  function busSend (actions, toAll) {
    if (toAll) {
      MessageBus.send(busType, { actions: actions })
    } else {
      MessageBus.send(busType, { actions: actions, messageBusKey: listenKey })
    }
  }

  return [busSend]
}

// HOC's
export function MessageBusWrapper (options) {
  let queryWrapper = (lopts) => {
    return (WrappedComponent) => {
      let opts = _.merge({
        key: undefined,
        listenForAllMessages: false,
        busType: MessageBusTypes.DrawEvents
      }, lopts)

      if (!opts.key) {
        opts.key = uuidv4()
      }

      class MessageBusInternal extends React.Component {
        myRef = React.createRef()
        messageBusCancel = null

        getWrappedInstance () {
          return this.myRef.current
        }

        componentDidMount () {
          this.messageBusCancel = MessageBus.subscribe(opts.busType, this.onEvents.bind(this))
        }

        onEvents (events) {
          if (this.myRef.current) {
            if (_.isFunction(this.myRef.current.onNewMessage)) {
              _.forEach(events.actions, (e) => {
                if (opts.listenForAllMessages === false && events.messageBusKey === opts.key) {
                  this.myRef.current.onNewMessage(e)
                } else if (opts.listenForAllMessages === true) {
                  this.myRef.current.onNewMessage(e)
                }
              })
            } else {
              log.Error('MBH No Ref.onNewMessage')
            }
          } else {
            log.Error('MBH No Ref')
          }
        }

        componentWillUnmount () {
          if (_.isFunction(this.messageBusCancel)) {
            this.messageBusCancel()
          }
        }

        render () {
          return (
            <WrappedComponent ref={this.myRef} {...this.props} MessageBusKey={opts.key} />
          )
        }
      }

      return MessageBusInternal
    }
  }

  if (_.isFunction(options)) {
    return queryWrapper({})(options)
  }

  return queryWrapper(options)
}

export function MessageBusSubWrapper (options) {
  let queryWrapper = (lopts) => {
    return (WrappedComponent) => {
      let opts = _.merge({
        key: undefined,
        listenForAllMessages: false,
        busType: MessageBusTypes.DrawEvents
      }, lopts)

      if (!opts.key) {
        opts.key = uuidv4()
      }

      class MessageBusSubInternal extends React.Component {
        ref = null
        messageBusCancel = null

        getWrappedInstance () {
          return this.ref
        }

        componentDidMount () {
          this.messageBusCancel = MessageBus.subscribe(opts.busType, this.onEvents.bind(this))
        }

        onEvents (events) {
          if (this.ref) {
            if (_.isFunction(this.ref.onNewMessage)) {
              _.forEach(events.actions, (e) => {
                if (opts.listenForAllMessages === false && events.messageBusKey === opts.key) {
                  this.ref.onNewMessage(e)
                } else if (opts.listenForAllMessages === true) {
                  this.ref.onNewMessage(e)
                }
              })
            } else {
              console.log('MBH No Ref.onNewMessage')
            }
          } else {
            console.log('MBH No Ref')
          }
        }

        componentWillUnmount () {
          if (_.isFunction(this.messageBusCancel)) {
            this.messageBusCancel()
          }
        }

        render () {
          return (
            <WrappedComponent ref={(c) => { this.ref = c }} {...this.props} MessageBusKey={opts.key} />
          )
        }
      }

      return MessageBusSubInternal
    }
  }

  if (_.isFunction(options)) {
    return queryWrapper({})(options)
  }

  return queryWrapper(options)
}

export function MessageBusPubWrapper (options) {
  let queryWrapper = (lopts) => {
    return (WrappedComponent) => {
      let opts = _.merge({
        busType: MessageBusTypes.DrawEvents
      }, lopts)

      class MessageBusPubInternal extends React.Component {
        ref = null

        getWrappedInstance () {
          return this.ref
        }

        sendMessage (actions, key) {
          try {
            const { MessageBusKey } = this.props
            MessageBus.send(opts.busType, { actions: actions, messageBusKey: key || MessageBusKey })
          } catch (err) {
            console.log('sendMessage Action CallBack Error:', err)
          }
        }

        render () {
          return (
            <WrappedComponent ref={(c) => { this.ref = c }} {...this.props} SendBusMessage={this.sendMessage} />
          )
        }
      }

      return MessageBusPubInternal
    }
  }

  if (_.isFunction(options)) {
    return queryWrapper({})(options)
  }

  return queryWrapper(options)
}

export function SendDrawBusMessage (message) {
  try {
    MessageBus.send(MessageBusTypes.DrawEvents, { actions: [message], messageBusKey: undefined })
  } catch (err) {
    console.log('sendMessage Action CallBack Error:', err)
  }
}

export const BuildMessageBusEvents = (componentName, customEvents) => {
  const ret = {
    Success: componentName + '.Success',
    NewSuccess: (payload) => {
      return { Type: componentName + '.Success', Payload: payload }
    },
    Error: componentName + '.Error',
    NewError: (payload) => {
      return { Type: componentName + '.Error', Payload: payload }
    },
    Closed: componentName + '.Closed',
    NewClosed: (payload) => {
      return { Type: componentName + '.Closed', Payload: payload }
    },
    Open: componentName + '.Open',
    NewOpen: (payload) => {
      return { Type: componentName + '.Open', Payload: payload }
    }
  }

  if (_.isArray(customEvents)) {
    for (var customEvent of customEvents) {
      const name = componentName + '.' + customEvent
      ret[customEvent] = name
      ret[`New${customEvent}`] = (payload) => {
        return { Type: name, Payload: payload }
      }
    }
  }

  return ret
}
